import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { createPortal } from 'react-dom';
import { bodyPortal } from '../../../../App';
import useIsDisplayedIsVisible from '../../../../hooks/useIsDisplayedIsVisible';

export type OverlayMethods = {
  updateMinHeight?: (minHeight?: number) => void;
};
interface IProps {
  isVisible: boolean;
  methodsRef?: React.MutableRefObject<OverlayMethods>;
  children: React.ReactNode;
  onClose: () => void;
  onClosed?: () => void;
}
const OptionsOverlay = ({ isVisible: isVisibleProp, methodsRef, children, onClose, onClosed }: IProps) => {
  const { isDisplayed, isVisible } = useIsDisplayedIsVisible(isVisibleProp, 250);

  const [minHeight, setMinHeight] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const updateMinHeight = useCallback((newMinHeight?: number) => {
    if (newMinHeight !== undefined) {
      setMinHeight(newMinHeight);
      return;
    }
    const rect = panelRef.current?.getBoundingClientRect();
    setMinHeight(rect?.height || 0);
  }, []);
  if (methodsRef) {
    methodsRef.current.updateMinHeight = updateMinHeight;
  }

  useEffect(() => {
    if (isDisplayed) {
      return;
    }

    onClosed?.();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDisplayed]);

  const Overlay = (
    <S.Container isDisplayed={isDisplayed} isVisible={isVisible}>
      <S.Backdrop onClick={onClose} />
      <S.Panel minHeight={minHeight} ref={panelRef}>
        {children}
      </S.Panel>
    </S.Container>
  );

  return createPortal(Overlay, bodyPortal.current);
};
export default memo(OptionsOverlay);

const S = {
  Container: styled.div<{ isDisplayed: boolean; isVisible: boolean }>`
    position: fixed;
    top: 0;
    left: 0;
    display: ${({ isDisplayed }) => (isDisplayed ? 'flex' : 'none')};
    justify-content: center;
    align-items: center;
    padding: 24px;
    width: 100%;
    height: 100%;
    color: ${({ theme }) => theme.colors.lightGray2};
    font-size: 14px;
    font-family: ${({ theme }) => theme.fontFamilies.Arimo};
    opacity: ${({ isVisible }) => (isVisible ? '1' : '0')};
    transform: scale(${({ isVisible }) => (isVisible ? '1' : '0.8')});
    transition: transform 150ms ease-in-out, opacity 150ms ease-in-out;
    cursor: default;
  `,
  Backdrop: styled.div`
    z-index: 0;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
  `,
  Panel: styled.div<{
    minHeight: number;
  }>`
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 350px;
    min-height: ${({ minHeight }) => minHeight}px;
    max-height: 80vh;
    border-radius: 12px;
    background-color: ${({ theme }) => theme.colors.darkGray10}};
    box-shadow: 0px 4px 4px 0px ${({ theme }) => theme.colors.transparentBlack2};
    overflow: hidden;
  `,
};
